import { Injectable } from '@angular/core';

import html2canvas from 'html2canvas';

const CAPTURE_TIMEOUT_MS = 5000;

/**
 * CSS class for opting an element out of the diagnostic screenshot.
 * Sensitive surfaces (impersonation banner, internal admin chrome,
 * any future surface with user-controlled secrets) can add this class
 * — `html2canvas` will skip it during capture.
 */
export const DIAGNOSTIC_REDACT_CLASS = 'diagnostic-redact';

export interface ScreenshotRedactionRule {
  selector: string;
  replacement: string;
}

/**
 * Content-level redactions applied before each html2canvas pass and
 * reverted in a `finally` block. Complements the structural
 * `.diagnostic-redact` opt-out class: this list catches sensitive
 * field VALUES that the consuming UI did not (or could not) mark
 * structurally — most importantly `<input type="password">`, whose
 * `value` is plain-text in the DOM even when the browser visually
 * masks it.
 *
 * Add a rule here when a privacy-sensitive value is present in the
 * DOM frequently enough that requiring every site to opt-out with
 * `.diagnostic-redact` would be unrealistic.
 */
export const SCREENSHOT_REDACTION_RULES: ReadonlyArray<ScreenshotRedactionRule> =
  [
    { selector: 'input[type="password"]', replacement: '***' },
    // Cover password fields rendered as type="text" (Material show/hide
    // toggle, legacy / a11y-oriented form variants). The
    // autocomplete-attribute hint is a stable signal from the markup.
    { selector: 'input[autocomplete="current-password"]', replacement: '***' },
    { selector: 'input[autocomplete="new-password"]', replacement: '***' },
    // Caller-explicit attribute opt-in for non-password sensitive values
    // (e.g. account-balance display elements). Complements the
    // structural `.diagnostic-redact` class — that hides the whole
    // subtree; this swaps only the rendered value.
    { selector: '[data-sensitive-value]', replacement: '***' },
  ];

@Injectable({ providedIn: 'root' })
export class ScreenshotService {
  /**
   * Capture the current document body as a PNG `Blob`. Returns `null`
   * on any failure (html2canvas throw, tainted canvas from cross-origin
   * images, memory pressure, timeout). The bundle flow proceeds without
   * a screenshot rather than failing entirely.
   *
   * A 5-second timeout caps pathological cases (e.g. an unusually deep
   * DOM that html2canvas spins on); the bundle is more valuable than a
   * pixel-perfect screenshot.
   *
   * The capture targets `document.body` deliberately — the entire
   * scrolled page is preserved, not just the current viewport. This is
   * occasionally richer than what the user explicitly mentioned in
   * their description (e.g. a list item off-screen below the fold).
   *
   * Elements carrying the `.diagnostic-redact` class are omitted from
   * the capture so sensitive UI can opt out without changing this
   * service.
   */
  async capture(): Promise<Blob | null> {
    try {
      const canvas = await this.withTimeout(
        this.runHtml2Canvas(),
        CAPTURE_TIMEOUT_MS
      );
      if (!canvas) {
        return null;
      }
      return await this.canvasToBlob(canvas);
    } catch {
      return null;
    }
  }

  /** Overridable for tests — wraps the external html2canvas call. */
  protected async runHtml2Canvas(): Promise<HTMLCanvasElement> {
    // Apply content-level redactions (e.g. password input values) before
    // the capture; the restorers run in `finally` so any rejection from
    // html2canvas still restores the live DOM.
    const restorers = ScreenshotService.applyRedactionRules();
    try {
      return await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        // scale: 1 caps the output at logical CSS pixels rather than the
        // device pixel ratio (often 2 on retina) — text stays readable
        // while the PNG is ~1/4 the size on high-DPI displays, easing
        // mail attachment limits.
        scale: 1,
        ignoreElements: ScreenshotService.shouldIgnoreElement,
      });
    } finally {
      restorers.forEach((restore) => restore());
    }
  }

  /**
   * Apply every {@link SCREENSHOT_REDACTION_RULES} entry to the live
   * DOM. For form inputs (`<input>`, `<textarea>`), swap the `.value`
   * to the rule's replacement AND set `disabled = true` for the
   * capture window — this prevents a FormControl race where a user
   * typing mid-capture would commit `'***' + chars` to the form
   * state. For every other matched element, swap `.textContent` so
   * caller-explicit attributes like `[data-sensitive-value]` on a
   * `<span>` also redact. Returns one restorer function per modified
   * element. Static so the spec can verify the contract without
   * instantiating html2canvas.
   *
   * The `el.value =` / `el.textContent =` writes are direct property
   * mutations: Angular FormControl listeners (one-way: FormControl →
   * DOM) are not triggered. Browser password managers / autofill
   * observers do not typically react either (they hook into
   * `input`/`change` or form-submit, not passive value swaps).
   */
  static applyRedactionRules(): Array<() => void> {
    const restorers: Array<() => void> = [];
    for (const rule of SCREENSHOT_REDACTION_RULES) {
      const elements = Array.from(document.querySelectorAll(rule.selector));
      for (const el of elements) {
        if (ScreenshotService.isFormInputElement(el)) {
          const originalValue = el.value;
          const wasDisabled = el.disabled;
          el.value = rule.replacement;
          el.disabled = true;
          restorers.push(() => {
            el.value = originalValue;
            el.disabled = wasDisabled;
          });
        } else {
          const originalText = el.textContent;
          el.textContent = rule.replacement;
          restorers.push(() => {
            el.textContent = originalText;
          });
        }
      }
    }
    return restorers;
  }

  private static isFormInputElement(
    el: Element
  ): el is HTMLInputElement | HTMLTextAreaElement {
    return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
  }

  /** Overridable for tests — wraps the canvas → blob conversion. */
  protected canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png');
    });
  }

  /**
   * Resolves with `null` on timeout. Rejections of the inner promise
   * propagate through (the outer `capture()` `try/catch` is the single
   * place that maps any failure to `null`).
   */
  protected withTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number
  ): Promise<T | null> {
    return new Promise<T | null>((resolve, reject) => {
      const timer = setTimeout(() => resolve(null), timeoutMs);
      promise.then(
        (value) => {
          clearTimeout(timer);
          resolve(value);
        },
        (err) => {
          clearTimeout(timer);
          reject(err);
        }
      );
    });
  }

  /**
   * `html2canvas` `ignoreElements` predicate. Static so the spec can
   * test the redaction contract without instantiating html2canvas.
   */
  static shouldIgnoreElement(el: Element): boolean {
    return el.classList.contains(DIAGNOSTIC_REDACT_CLASS);
  }
}
